// src/pages/PrivacyPolicy.js
import React from "react";
import "./PrivacyPolicy.css";
import { Link } from "react-router-dom";

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <h1 className="text-center">Privacy Policy — Edgar Lopez Law</h1>
      <p className="text-center">
        <em>Effective Date: January 1, 2026</em>
      </p>

      <section>
        <h2>1. Who We Are</h2>
        <p>
          Edgar Lopez Law ("we," "us," or "our") provides legal services focused on personal injury
          matters. This Privacy Policy explains how we collect, use, disclose, and protect
          information when you visit our website, request a consultation, submit intake information,
          communicate with us, or access any client portal or case-status tools we provide
          (collectively, the “Services”).
        </p>
      </section>

      <section>
        <h2>2. Scope</h2>
        <p>
          This Policy applies to: (a) website visitors; (b) prospective clients; (c) clients;
          (d) people who communicate with us on behalf of someone else (for example, a family member);
          and (e) authorized users of any portal features.
        </p>
      </section>

      <section>
        <h2>3. Information We Collect</h2>

        <h3>3.1 Information You Provide</h3>
        <ul>
          <li>
            <strong>Contact details</strong> (name, phone number, email address, mailing address).
          </li>
          <li>
            <strong>Case intake information</strong> (incident details, dates, locations, involved
            parties, insurance details, injury information, and related facts you choose to share).
          </li>
          <li>
            <strong>Documents and media</strong> you upload or send (photos, videos, PDFs, medical
            bills/records, insurance correspondence, police reports, and other supporting materials),
            plus file metadata (filename, size, timestamps).
          </li>
          <li>
            <strong>Portal/account information</strong> (if you create or access a portal): username,
            hashed password, role/access level, and access logs.
          </li>
          <li>
            <strong>Communications</strong> (messages, emails, form submissions, call notes, and—where
            lawful—recorded calls for training/quality).
          </li>
        </ul>

        <h3>3.2 Information Collected Automatically</h3>
        <p>
          We may automatically collect device and usage information such as IP address, browser type,
          device identifiers, pages viewed, referring URLs, and timestamps. We use this information
          for security, fraud prevention, troubleshooting, analytics, and to maintain audit logs.
        </p>

        <h3>3.3 Information From Third Parties</h3>
        <p>
          Where lawful and appropriate, we may receive information from third parties such as
          insurance companies, medical providers, investigators, court/public records, and service
          providers (for example, scheduling or communications vendors) to help evaluate,
          investigate, and manage matters.
        </p>
      </section>

      <section>
        <h2>4. How We Use Information</h2>
        <ul>
          <li>Respond to consultation requests, inquiries, and messages.</li>
          <li>Evaluate potential matters, run conflict checks, and determine whether we can assist.</li>
          <li>Schedule consultations and communicate about your inquiry or case.</li>
          <li>Provide legal services (only after a signed engagement agreement is in place).</li>
          <li>Operate the Services, including any portal, and maintain security and audit logs.</li>
          <li>Improve our website and user experience, including basic analytics.</li>
          <li>Comply with legal obligations and professional responsibilities.</li>
        </ul>
      </section>

      <section>
        <h2>5. Legal Basis &amp; Consent</h2>
        <p>
          We process personal information to respond to your requests, to take steps at your request
          before entering into a representation agreement, to perform a contract (if we represent
          you), to comply with legal obligations, and for legitimate interests such as securing our
          systems and improving our Services. Where required by law (for example, certain marketing
          communications), we will obtain consent, which you may withdraw at any time.
        </p>
      </section>

      <section>
        <h2>6. SMS, Email &amp; Calls</h2>
        <p>
          If you provide your phone number or email, you agree we may contact you about scheduling,
          intake, your inquiry, and case-related updates. We may also send limited marketing
          messages where permitted by law. Message frequency may vary; message and data rates may
          apply. You can opt out of marketing SMS at any time by replying STOP or following
          unsubscribe instructions.
        </p>
        <p>
          We may use third-party communications providers to deliver SMS, calls, and email. We do
          not sell your mobile opt-in data.
        </p>
      </section>

      <section>
        <h2>7. Sharing &amp; Disclosures</h2>
        <ul>
          <li>
            <strong>Service providers:</strong> vendors who provide hosting, communications,
            scheduling, document storage, analytics, and IT/security support under appropriate
            confidentiality and data-protection terms.
          </li>
          <li>
            <strong>Legal &amp; compliance:</strong> when required by law, subpoena, court order, or to
            protect rights, safety, and security.
          </li>
          <li>
            <strong>With your direction/authorization:</strong> for example, sharing information with
            medical providers, experts, investigators, or others as needed for your matter.
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger, acquisition, asset sale,
            or reorganization, subject to this Policy or an equivalent level of protection.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Client Portal, Role-Based Access &amp; Audit Logs</h2>
        <p>
          If we offer portal features, access may be controlled through role-based access control
          (RBAC). Access to records may be logged (user identity, time, IP/device, and key actions).
          We may review and investigate suspicious activity and may suspend or terminate access
          where misuse is detected.
        </p>
      </section>

      <section>
        <h2>9. Security</h2>
        <ul>
          <li>Encryption in transit (TLS/HTTPS) for our website and Services.</li>
          <li>Access controls and least-privilege principles for systems under our control.</li>
          <li>Credential hashing for stored passwords and session protections where applicable.</li>
          <li>Backups and reasonable security patching/maintenance.</li>
        </ul>
        <p>
          <em>No security method is 100% guaranteed.</em> If we discover a security incident that
          affects your information, we will follow applicable notification requirements.
        </p>
      </section>

      <section>
        <h2>10. Retention</h2>
        <p>
          We retain information as long as reasonably necessary for the purposes described in this
          Policy, to provide Services, to meet professional/legal obligations, to resolve disputes,
          and to enforce agreements. When information is no longer needed, we take reasonable steps
          to delete, anonymize, or de-identify it.
        </p>
      </section>

      <section>
        <h2>11. Cookies &amp; Analytics</h2>
        <p>
          We use essential cookies for security and functionality, and limited analytics to improve
          our website. You can control cookies through your browser settings. Third-party tools may
          process data under their own policies.
        </p>
      </section>

      <section>
        <h2>12. Children &amp; Minors</h2>
        <p>
          Our Services are not directed to children under 13. If you believe a child has provided
          personal information to us without appropriate consent, please contact us.
        </p>
      </section>

      <section>
        <h2>13. Your Rights (California Notice)</h2>
        <p>
          Depending on where you live, you may have rights to request access to, correction of, or
          deletion of your personal information, and to limit or object to certain processing,
          subject to legal exceptions. California residents may have additional rights under the
          California Consumer Privacy Act (as amended). To exercise a request, contact us using the
          details below. We may need to verify your identity and authority.
        </p>
        <p>
          We do not sell personal information as that term is defined under California law.
        </p>
      </section>

      <section>
        <h2>14. Third-Party Links</h2>
        <p>
          Our website may contain links to external sites. We are not responsible for their content
          or privacy practices. Review their policies before providing information.
        </p>
      </section>

      <section>
        <h2>15. Changes to This Policy</h2>
        <p>
          We may update this Policy from time to time. The effective date above will change when
          updates are posted. Your continued use of the Services after updates constitutes
          acceptance.
        </p>
      </section>

      <section>
        <h2>16. Contact Us</h2>
        <p>
          Edgar Lopez Law<br />
          Phone: <a href="tel:+19517492948">(951) 749-2948</a><br />
          Email: <a href="mailto:attorney@edgarlopezlaw.com">attorney@edgarlopezlaw.com</a>
        </p>
        <p>
          For related terms governing use of the website, see our <Link to="/terms">Terms &amp; Conditions</Link>.
          For how to reach us or send a message, visit our <Link to="/contact">Contact page</Link>.
        </p>
      </section>

      <p className="text-center" style={{ marginTop: 18, opacity: 0.85 }}>
        <em>
          Important: Do not send confidential or time-sensitive information through this website
          until an attorney-client relationship has been established.
        </em>
      </p>
    </div>
  );
}

export default PrivacyPolicy;